import axios from 'axios';
import { endOfYear, format, startOfYear, subDays } from 'date-fns';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { CompanyAccountingEntries } from '../../models/company.accounting.entries.model';
import { AccountingEntry } from '../../models/accounting.entry.model';
import { CompanyAccountingContactEntries } from '../../models/company.accounting.contact.entries.model';
import { CompanyAccountingBookingJournalEntries } from '../../models/company.accounting.booking.journal.entries.model';
import { CompanyAccountingAccountEntries } from '../../models/company.accounting.account.entries.model';
import { AccountingEntryReference } from '../../models/accounting.entry.reference.model';
import { Message } from '../../models/message.model';
import { authToken } from '../../scripts/authentication.script';
import { fetchExportBlob, BlobExportResult } from '../../scripts/export.blob.script';

export class AccountingEntryDA {
  static apiResponse: ApiResponse

  public static async getAccountingCreditIncompleteEntries(
    id_company: string,
    id_contact?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingEntries: CompanyAccountingEntries | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.entries.credit.incomplete.data.json').then(response => { companyAccountingEntries = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingEntries!.id_company === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingEntries
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/available?type=credit&account_code_prefix=11${id_contact ? `&id_contact=${encodeURIComponent(id_contact)}` : ''}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        const items = response.data?.data?.items ?? []
        const accountingEntries: AccountingEntry[] = items.map((item: any) => ({
          id: item.id,
          id_company: id_company,
          id_booking_journal: '',
          id_contact: item.id_contact ?? '',
          type: '',
          description: item.description ?? '',
          date: item.date,
          created_at: item.date,
          updated_at: item.date,
          completed: false,
          automated: false,
          balance: item.available_amount,
          debits: item.debit_line ? [item.debit_line] : [],
          credits: item.credit_line ? [item.credit_line] : [],
        }))

        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: { id_company, accounting_entries: accountingEntries }
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getAccountingDebitIncompleteEntries(
    id_company: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingEntries: CompanyAccountingEntries | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.entries.debit.incomplete.data.json').then(response => { companyAccountingEntries = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingEntries!.id_company === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingEntries
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/debit/incomplete`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getAccountingEntryById(
    id_company: string,
    id_entry: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getAccountingContactEntries(
    id_company: string,
    id_contact: string,
    interval: string,
    start_date: string,
    end_date: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingContactEntries: CompanyAccountingContactEntries | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.entries.contact.data.json').then(response => { companyAccountingContactEntries = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingContactEntries!.id_company === id_company && companyAccountingContactEntries!.id_contact === id_contact) {
        companyAccountingContactEntries!.filter = { interval: interval, start_date: start_date, end_date: end_date }

        if (start_date.length > 0) {
          companyAccountingContactEntries!.balance_previous.date = format(subDays(new Date(start_date), 1), 'yyyy-MM-dd')
          companyAccountingContactEntries!.balance_previous.amount = Math.floor(Math.random() * 100000)
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingContactEntries
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const params = new URLSearchParams({
        ...(interval && { interval }),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        sort_by: 'date',
        sort_order: 'asc',
        page: String(page),
        limit: String(limit),
      })
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/by-contact/${id_contact}?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getAccountingAccountEntries(
    id_company: string,
    id_accounting_account: string,
    interval: string,
    start_date: string,
    end_date: string,
    page: number = 1,
    limit: number = 20,
    reconciled: string = 'all',
    include_opening_balance?: boolean
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingAccountEntries: CompanyAccountingAccountEntries | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.entries.accounting.account.data.json').then(response => { companyAccountingAccountEntries = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingAccountEntries!.id_company === id_company && companyAccountingAccountEntries!.id_accounting_account === id_accounting_account) {
        companyAccountingAccountEntries!.filter = { interval: interval, start_date: start_date, end_date: end_date }

        if (start_date.length === 0 && end_date.length === 0) {
          companyAccountingAccountEntries!.filter.start_date = `${format(startOfYear(new Date()), 'yyyy-MM-dd')}`
          companyAccountingAccountEntries!.filter.end_date = `${format(endOfYear(new Date()), 'yyyy-MM-dd')}`
        }
        if (start_date.length > 0) {
          companyAccountingAccountEntries!.balance_previous.date = format(subDays(new Date(start_date), 1), 'yyyy-MM-dd')
          companyAccountingAccountEntries!.balance_previous.amount = Math.floor(Math.random() * 100000)
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingAccountEntries
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const accountParams = new URLSearchParams({
        ...(interval && { interval }),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        sort_by: 'date',
        sort_order: 'asc',
        page: String(page),
        limit: String(limit),
        ...(reconciled && reconciled !== 'all' && { reconciled }),
        ...(include_opening_balance === false && { include_opening_balance: 'false' }),
      })
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/by-account/${id_accounting_account}?${accountParams.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        const rawData = response.data?.data ?? response.data

        // Adapt new backend response { items, pagination, opening_balance, period_totals, closing_balance }
        // to the frontend model CompanyAccountingAccountEntries expected by PlanView.
        const adapted = {
          id_company: id_company,
          id_accounting_account: id_accounting_account,
          filter: {
            interval: interval ?? '',
            start_date: start_date ?? '',
            end_date: end_date ?? ''
          },
          balance_previous: {
            date: start_date ?? '',
            amount: rawData?.page_opening_balance?.balance ?? rawData?.opening_balance?.balance ?? 0
          },
          opening_balance: rawData?.opening_balance ?? null,
          opening_balance_original: rawData?.opening_balance_original ?? null,
          include_opening_balance: rawData?.include_opening_balance,
          accounting_entries: rawData?.items ?? [],
          pagination: rawData?.pagination ?? { page: 1, limit: 20, total: 0, pages: 1 },
          period_totals: rawData?.period_totals ?? { debit: 0, credit: 0 },
          closing_balance: rawData?.closing_balance ?? { debit: 0, credit: 0, balance: 0 },
        }

        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: adapted
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getAccountingBookingJournalEntries(
    id_company: string,
    id_booking_journal: string,
    accounting_account_id: string,
    interval: string,
    start_date: string,
    end_date: string,
    page: number = 1,
    limit: number = 20,
    q: string = ''
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingBookingJournalEntries: CompanyAccountingBookingJournalEntries | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.entries.booking.journal.data.json').then(response => { companyAccountingBookingJournalEntries = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingBookingJournalEntries!.id_company === id_company && companyAccountingBookingJournalEntries!.id_booking_journal === id_booking_journal) {
        companyAccountingBookingJournalEntries!.filter = { accounting_account: accounting_account_id, interval: interval, start_date: start_date, end_date: end_date }

        if (start_date.length === 0 && end_date.length === 0) {
          companyAccountingBookingJournalEntries!.filter.start_date = `${format(startOfYear(new Date()), 'yyyy-MM-dd')}`
          companyAccountingBookingJournalEntries!.filter.end_date = `${format(endOfYear(new Date()), 'yyyy-MM-dd')}`
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingBookingJournalEntries
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries`

      let config: any = {
        headers: { Authorization: authToken() },
        params: {
          ...(id_booking_journal && { id_journal: id_booking_journal }),
          ...(accounting_account_id && { id_account: accounting_account_id }),
          ...(start_date && { date_from: start_date }),
          ...(end_date && { date_to: end_date }),
          ...(q && { q }),
          page,
          limit,
        }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postAccountingTransactionEntry(
    id_company: string,
    transaction: string,
    contact_id: string,
    sub_type: string,
    treasury_account_id: string,
    concept_account_id: string,
    tax_code: string,
    tax_rate: number,
    tax_account_id: string,
    payment_method: string,
    date: string,
    amount: string,
    _company_tax: { subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/transaction/${transaction}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        contact: contact_id,
        sub_type: sub_type,
        treasury_account: treasury_account_id,
        concept_account: concept_account_id,
        payment_method: payment_method,
        tax_code: tax_code,
        tax_rate: tax_rate,
        tax_account: tax_account_id,
        date: date,
        amount: amount
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postEntry(
    id_company: string,
    id_journal: string,
    date: string,
    type: string,
    description: string,
    debits: { id_account: string, amount: number, description: string, amount_original?: number, exchange_rate?: number, reference?: { tax_rates: string[] } }[],
    credits: { id_account: string, amount: number, description: string, amount_original?: number, exchange_rate?: number, reference?: { tax_rates: string[] } }[],
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    let data: any = {
      id_journal,
      description,
      date,
      type,
      debits,
      credits,
    }

    await axios.post(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async postSimplifiedEntry(
    id_company: string,
    id_journal: string,
    date: string,
    description: string,
    concept_account: string,
    counterpart_account: string,
    amount: number,
    tax_included: boolean,
    tax_code: string,
    transaction: string,
    company_tax: { tax_method: string, tax_declaration: string, subject_tax: boolean },
    vat_input_type?: string,
    amount_original?: number,
    exchange_rate?: number,
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/simplified`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    let data: any = {
      id_journal,
      id_contact: null,
      description,
      date,
      concept_account,
      counterpart_account,
      amount,
      tax_included,
      tax_code,
      transaction,
    }

    if (vat_input_type) {
      data.vat_input_type = vat_input_type
    }

    if (amount_original != null && exchange_rate != null) {
      data.amount_original = amount_original
      data.exchange_rate = exchange_rate
    }

    await axios.post(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async putAccountingEntry(
    id_company: string,
    id_accounting_entry: string,
    contact_id: string,
    sub_type: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let accountingCreditEntries: AccountingEntry[] | null = null
      let accountingDebitEntries: AccountingEntry[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.entries.credit.incomplete.data.json').then(response => { accountingCreditEntries = response.data.accounting_entries }).catch(error => { alert(error) })
      await axios.get('../../../../../database/accounting.entries.debit.incomplete.data.json').then(response => { accountingDebitEntries = response.data.accounting_entries }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (accountingCreditEntries!.some(item => item.id === id_accounting_entry) || accountingDebitEntries!.some(item => item.id === id_accounting_entry)) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_accounting_entry}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        contact: contact_id,
        sub_type: sub_type
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putEntry(
    id_company: string,
    id_entry: string,
    description: string,
    date: string,
    debits: { id_account: string, amount: number, description: string, amount_original?: number, exchange_rate?: number, reference?: { tax_rates: string[] } }[],
    credits: { id_account: string, amount: number, description: string, amount_original?: number, exchange_rate?: number, reference?: { tax_rates: string[] } }[],
    extra?: { id_bank_statement?: string, id_bank_statement_line?: string },
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    let data: any = {
      description,
      date,
      debits,
      credits,
      ...extra,
    }

    await axios.put(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async putSimplifiedEntry(
    id_company: string,
    id_entry: string,
    id_journal: string,
    date: string,
    description: string,
    concept_account: string,
    counterpart_account: string,
    amount: number,
    tax_included: boolean,
    tax_code: string,
    transaction: string,
    company_tax: { tax_method: string, tax_declaration: string, subject_tax: boolean },
    vat_input_type?: string,
    amount_original?: number,
    exchange_rate?: number,
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}/simplified`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    let data: any = {
      id_journal,
      id_contact: null,
      description,
      date,
      concept_account,
      counterpart_account,
      amount,
      tax_included,
      tax_code,
      transaction,
    }

    if (vat_input_type) {
      data.vat_input_type = vat_input_type
    }

    if (amount_original != null && exchange_rate != null) {
      data.amount_original = amount_original
      data.exchange_rate = exchange_rate
    }

    await axios.put(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async uploadVoucher(
    id_company: string,
    id_entry: string,
    file: File,
    role?: string,
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}/vouchers/upload`

    const formData = new FormData()
    formData.append('file', file)
    formData.append('source', 'web')
    if (role) formData.append('role', role)

    let config: any = {
      headers: {
        Authorization: authToken(),
        'Content-Type': 'multipart/form-data'
      }
    }

    await axios.post(url, formData, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async attachVoucher(
    id_company: string,
    id_entry: string,
    body: { id_document_inbox: string },
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}/vouchers`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.post(url, body, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async deleteVoucher(
    id_company: string,
    id_entry: string,
    index: number,
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}/vouchers/${index}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getVoucherUrl(
    id_company: string,
    id_entry: string,
    index: number,
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}/vouchers/${index}/url`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async createEntry(
    id_company: string,
    data: {
      id_journal: string;
      id_contact?: string | null;
      description: string;
      date: string;
      type: string;
      debits: Array<{ id_account: string; amount: number; description?: string; reference?: any }>;
      credits: Array<{ id_account: string; amount: number; description?: string; reference?: any }>;
    }
  ): Promise<ApiResponse> {
    const { company_tax: _ct, ...payload } = data as any
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.post(url, payload, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async deleteEntry(
    id_company: string,
    id_entry: string,
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries/${id_entry}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async exportAccountEntries(
    id_company: string,
    id_account: string,
    format: 'xlsx' | 'pdf',
    params: { interval?: string; start_date?: string; end_date?: string; reconciled?: string; lang?: string; include_opening_balance?: string } = {},
    fallbackName?: string
  ): Promise<BlobExportResult> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts/${id_account}/export`
    return fetchExportBlob(url, {
      format,
      interval: params.interval,
      start_date: params.start_date,
      end_date: params.end_date,
      reconciled: params.reconciled,
      lang: params.lang,
      ...(params.include_opening_balance !== undefined && { include_opening_balance: params.include_opening_balance }),
    }, fallbackName)
  }
}
